import DateTimeFormatter from '@Components/DateTimeFormatter/DateTimeFormatter';
import MainTable from '@Components/MainTable';
import { MainTableRow } from '@Components/MainTable/MainTable';
import NoResults from '@Components/NoResults/NoResults';
import { TableHeader } from '@Features/generics/generics.type';
import UnEnrollementModel from '@Pages/ClassRooms/Pages/SingleCourse/Components/EnrollmentModel/UnEnroolmentModel';
import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import { FlexLayout } from '@Styled/utilities';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Chip, IconButton, TableCell, Tooltip } from '@mui/material';
import React, { useState } from 'react';
import styled from 'styled-components';
import {
	BanButton,
	errorMessage,
	StatusChip,
	SubscriptionStatus,
	useSubscriptionGrants,
} from './subscriptions.shared';
import useStudentList from './useStudentList';

type CourseRow = {
	type?: 'standalone';
	id: number;
	title: string;
	code: string;
	price: number;
	instructor_name: string;
	enrollment_id: number | null;
	purchase_method: string | null;
	source_bundle_id: number | null;
	source_bundle_title: string | null;
	enrollment_active: boolean | null;
	activate_until: Date | null;
	enrolled_at: Date | null;
	status: SubscriptionStatus;
	progress: number;
	refundable_amount: number;
};

type BundleRow = {
	type: 'bundle';
	id: number;
	title: string;
	code: string;
	price: number;
	instructor_name: string;
	enrollment_id: number;
	purchase_method: string | null;
	enrollment_active: boolean | null;
	enrolled_at: Date | null;
	status: SubscriptionStatus;
	progress: number;
	courses_count: number;
	refundable_amount: number;
	members: CourseRow[];
};

type SubscriptionRow = BundleRow | (CourseRow & { type: 'standalone' });

type Target = { kind: 'course' | 'bundle'; row: CourseRow | BundleRow };

const TABLE_HEADS: TableHeader[] = [
	{ displayName: 'SUBSCRIPTION', fieldName: 'title', canSort: false, width: '24%' },
	{ displayName: 'INSTRUCTOR', fieldName: 'instructor', canSort: false },
	{ displayName: 'PRICE', fieldName: 'price', canSort: false },
	{ displayName: 'METHOD', fieldName: 'purchase_method', canSort: false },
	{ displayName: 'ENROLLED AT', fieldName: 'enrolled_at', canSort: false },
	{ displayName: 'ACCESS UNTIL', fieldName: 'activate_until', canSort: false },
	{ displayName: 'STATUS', fieldName: 'status', canSort: false },
];

/** How a bundle member is held, as seen from that bundle's row. */
const memberAccess = (member: CourseRow, bundleId: number) => {
	if (!member.enrollment_id) return 'Not held';
	if (Number(member.source_bundle_id) === bundleId) return 'Via this bundle';
	if (member.source_bundle_id) return `Via bundle: ${member.source_bundle_title ?? member.source_bundle_id}`;
	return 'Owned individually';
};

const date = (value: Date | null | undefined) => (value ? <DateTimeFormatter date={value} /> : '-');

type Props = { studentId: number };

/**
 * Subscriptions → Enrollment: only what the student holds. A bundle row
 * expands to its member courses; courses held on their own are top-level.
 * Courses a bundle grants are managed through that bundle (the backend's
 * bundle engine keeps their access in step), so their own actions are off.
 */
const SubscriptionsTable: React.FC<Props> = ({ studentId }) => {
	const { displaySnackbar } = useSnackbar();
	const { isAdminPortal, canManage, canUnenroll } = useSubscriptionGrants();
	const { rows, meta, load, reload } = useStudentList<SubscriptionRow>(
		studentId,
		'subscriptions',
		[],
		'Unable to load subscriptions'
	);
	// one bundle open at a time
	const [expanded, setExpanded] = useState<number | null>(null);
	const [removing, setRemoving] = useState<Target>();
	const [refund, setRefund] = useState<boolean | undefined>();

	const toggle = (bundleId: number) => setExpanded((current) => (current === bundleId ? null : bundleId));

	const setStatus = async ({ kind, row }: Target, active: boolean) => {
		try {
			await api.post(
				kind === 'bundle' ? 'bundles/users/status' : 'standalone-courses/users/status',
				kind === 'bundle'
					? { bundle_id: row.id, user_id: studentId, active }
					: { standalone_course_id: row.id, user_id: studentId, active }
			);
			displaySnackbar(
				'success',
				active
					? 'Student unbanned'
					: kind === 'bundle'
					? 'Student banned — access to the bundle courses is suspended'
					: 'Student banned — the student loses access to this course'
			);
			reload();
		} catch (error: any) {
			displaySnackbar('error', errorMessage(error, 'Unable to update subscription status'));
		}
	};

	const closeRemove = () => {
		setRemoving(undefined);
		setRefund(undefined);
	};

	const remove = async () => {
		if (!removing || !isAdminPortal) return;
		const { kind, row } = removing;
		try {
			await api.delete(kind === 'bundle' ? 'bundles/users/remove' : 'standalone-courses/users/remove', {
				data:
					kind === 'bundle'
						? { bundle_id: row.id, user_id: studentId, refund: Boolean(refund) }
						: { standalone_course_id: row.id, user_id: studentId, refund: Boolean(refund) },
			});
			displaySnackbar(
				'success',
				refund ? 'Subscription removed and refund processed successfully' : 'Subscription removed successfully'
			);
			closeRemove();
			reload();
		} catch (error: any) {
			displaySnackbar('error', errorMessage(error, 'Unable to remove subscription'));
		}
	};

	/**
	 * Ban/unban + remove as compact icon buttons. Courses a bundle grants get
	 * none: they are managed only through their bundle.
	 */
	const renderActions = (target: Target, managedByBundle = false) => {
		const { row } = target;
		if (!row.enrollment_id || managedByBundle) return <TableCell />;
		const banned = row.enrollment_active === false;
		const kindLabel = target.kind === 'bundle' ? 'bundle' : 'course';
		return (
			<TableCell>
				<Actions>
					{canManage && (
						<Tooltip title={banned ? 'Unban student' : 'Ban student'} placement="top">
							<BanButton banned={banned} aria-label={banned ? 'Unban student' : 'Ban student'} onClick={() => setStatus(target, banned)} />
						</Tooltip>
					)}
					{canUnenroll && (
						<Tooltip title={`Remove ${kindLabel}`} placement="top">
							<ActionButton size="small" $danger aria-label={`Remove ${kindLabel}`} onClick={() => setRemoving(target)}>
								<PersonRemoveOutlinedIcon sx={{ fontSize: 22 }} />
							</ActionButton>
						</Tooltip>
					)}
				</Actions>
			</TableCell>
		);
	};

	const renderCourseRow = (course: CourseRow, parent?: BundleRow) => {
		return (
			<MainTableRow key={parent ? `b${parent.id}-c${course.id}` : `c${course.id}`} hover sx={parent ? { background: '#f7fbff' } : undefined}>
				<TableCell>
					<TitleCell $indent={Boolean(parent)}>
						<Tooltip title={course.code ? `${course.title} (${course.code})` : course.title} placement="top">
							<Title>{course.title}</Title>
						</Tooltip>
						<Note>
							{parent ? memberAccess(course, parent.id) : course.source_bundle_id ? `Via bundle: ${course.source_bundle_title ?? course.source_bundle_id}` : 'Standalone course'}
						</Note>
					</TitleCell>
				</TableCell>
				<TableCell>{course.instructor_name || '-'}</TableCell>
				<TableCell>{course.price}</TableCell>
				<TableCell>{course.purchase_method || '-'}</TableCell>
				<TableCell>{date(course.enrolled_at)}</TableCell>
				<TableCell>{!course.enrollment_id ? '-' : course.activate_until ? date(course.activate_until) : 'No limit'}</TableCell>
				<TableCell>
					<StatusChip status={course.status} />
				</TableCell>
				{renderActions({ kind: 'course', row: course }, Boolean(course.source_bundle_id))}
			</MainTableRow>
		);
	};

	const renderBundle = (bundle: BundleRow) => {
		const open = expanded === bundle.id;
		return (
			<React.Fragment key={`b${bundle.id}`}>
				<MainTableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggle(bundle.id)}>
					<TableCell>
						<TitleCell>
							<FlexLayout alignItems="center" gridGap="0.25rem">
								<IconButton size="small" aria-label={open ? 'Collapse bundle' : 'Expand bundle'}>
									{open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
								</IconButton>
								<Tooltip title={bundle.code ? `${bundle.title} (${bundle.code})` : bundle.title} placement="top">
									<Title>{bundle.title}</Title>
								</Tooltip>
							</FlexLayout>
							<Note $offset>
								<Chip label="Bundle" size="small" color="secondary" variant="outlined" sx={{ height: 18, fontSize: 11, mr: 0.75 }} />
								{bundle.courses_count} courses
							</Note>
						</TitleCell>
					</TableCell>
					<TableCell>{bundle.instructor_name || '-'}</TableCell>
					<TableCell>{bundle.price}</TableCell>
					<TableCell>{bundle.purchase_method || '-'}</TableCell>
					<TableCell>{date(bundle.enrolled_at)}</TableCell>
					<TableCell>-</TableCell>
					<TableCell>
						<StatusChip status={bundle.status} />
					</TableCell>
					<StopClick>{renderActions({ kind: 'bundle', row: bundle })}</StopClick>
				</MainTableRow>
				{open && bundle.members.map((member) => renderCourseRow(member, bundle))}
			</React.Fragment>
		);
	};

	const removingBundle = removing?.kind === 'bundle';

	return (
		<>
			{isAdminPortal && (
				<UnEnrollementModel
					handleCloseUnEnrolledModel={closeRemove}
					onUNEnroll={remove}
					setRefund={setRefund}
					openUnEnrolled={Boolean(removing)}
					modelText="student"
					confirmationText={`Are you sure you want to remove this student from ${removingBundle ? 'the bundle' : ''} "${
						removing?.row.title ?? ''
					}"? The refundable historical amount is ${removing?.row.refundable_amount || 0}. ${
						removingBundle
							? 'Course access granted by this bundle is removed too.'
							: 'Related Lecture enrollments will also be removed.'
					}`}
				/>
			)}
			<MainTable
				onPageChangeRequest={load}
				tableFilters={[]}
				tableHeads={TABLE_HEADS}
				totalPages={1}
				total={Number(meta.total || 0)}
				RowsperPage={Number(meta.per_page || 10)}
				pageNum={1}
				$hasActions
				renderItems={() =>
					rows.length === 0 ? (
						<MainTableRow>
							<TableCell colSpan={TABLE_HEADS.length + 1}>
								<NoResults />
							</TableCell>
						</MainTableRow>
					) : (
						rows.map((row) => (row.type === 'bundle' ? renderBundle(row) : renderCourseRow(row)))
					)
				}
			/>
		</>
	);
};

/** Actions inside the clickable bundle row must not toggle it. */
const StopClick: React.FC = ({ children }) => (
	<>
		{React.Children.map(children, (child) =>
			React.isValidElement(child)
				? React.cloneElement(child as React.ReactElement<any>, {
						onClick: (e: React.MouseEvent) => e.stopPropagation(),
				  })
				: child
		)}
	</>
);

const Actions = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.25rem;
`;
const ActionButton = styled(IconButton)<{ $danger?: boolean }>`
	&.MuiIconButton-root {
		color: #9e9e9e;
	}
	&.MuiIconButton-root:hover {
		color: ${({ $danger }) => ($danger ? '#f74343' : 'inherit')};
		background: ${({ $danger }) => ($danger ? 'rgba(247, 67, 67, 0.08)' : 'rgba(0, 0, 0, 0.05)')};
	}
	/* icons that carry their own colour (the ban ⊘) turn red on hover too */
	&.MuiIconButton-root:hover .MuiSvgIcon-root {
		color: ${({ $danger }) => ($danger ? '#f74343' : 'inherit')};
	}
`;
const TitleCell = styled.div<{ $indent?: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	text-align: start;
	gap: 0.15rem;
	padding-inline-start: ${({ $indent }) => ($indent ? '2.75rem' : '0')};
`;
const Title = styled.span`
	font-weight: 500;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;
const Note = styled.span<{ $offset?: boolean }>`
	display: flex;
	align-items: center;
	font-size: 0.75rem;
	color: #6e7577;
	padding-inline-start: ${({ $offset }) => ($offset ? '2.25rem' : '0')};
`;

export default SubscriptionsTable;
